import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';
import { ROLES, isValidRole } from '@wevo/wevo-access';

interface UserRoles {
    roles: string[];
    hasRole: (role: string) => boolean;
    isAdmin: boolean;
    isLoading: boolean;
}

export const useUserRoles = (): UserRoles => {
    const { user, isLoading: authLoading } = useAuth0();

    const roles = useMemo(() => {
        // Extract roles from Auth0 user metadata or custom claims
        // Check common locations where Auth0 stores role information
        const userRoles =
            user?.['wevo/roles'] || // Custom namespace for roles
            user?.app_metadata?.roles || // App metadata
            user?.user_metadata?.roles || // User metadata
            user?.roles || // Direct roles property
            [];

        // Ensure we return an array
        return Array.isArray(userRoles) ? userRoles : [];
    }, [user]);

    const hasRole = useCallback(
        (role: string): boolean => {
            return roles.includes(role) && isValidRole(role);
        },
        [roles],
    );

    const isAdmin = useMemo(() => {
        return hasRole(ROLES.ORGANIZATION_ADMIN);
    }, [hasRole]);

    return {
        roles,
        hasRole,
        isAdmin,
        isLoading: authLoading,
    };
};
